// Prevent Vercel Speed Insights from loading in dev / on localhost by blocking injected script elements
if (import.meta.env.MODE !== 'production' || ['localhost', '127.0.0.1'].includes(location.hostname)) {
  const _origAppendChild = Element.prototype.appendChild;
  Element.prototype.appendChild = function (child) {
    try {
      if (child && child.tagName === 'SCRIPT' && typeof child.src === 'string' && child.src.includes('va.vercel-scripts.com')) {
        console.debug('[dev] Blocked Vercel Speed Insights script:', child.src);
        return child; // don't append
      }
    } catch (e) {
      // ignore and fall through to default behavior
    }
    return _origAppendChild.call(this, child);
  };
}

import './assets/main.css'

import {createApp} from 'vue'
import { VueReCaptcha } from 'vue-recaptcha-v3'
import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import {definePreset} from '@primeuix/themes';
import App from './App.vue'
import router from './router'
import {createPinia} from 'pinia';
import * as Sentry from "@sentry/vue";

// FormKit
import {plugin} from '@formkit/vue'
import config from './formkit.config' // path to your config file

// LucideIcon Helper
import LucideIcon from './components/LucideIconComponent.vue'

// PrimeVue components
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import Avatar from 'primevue/avatar';
import Button from "primevue/button"
import BlockUI from 'primevue/blockui';
import Card from 'primevue/card';
import Chart from 'primevue/chart';
import ConfirmationService from 'primevue/confirmationservice';
import ConfirmDialog from 'primevue/confirmdialog';
import DatePicker from 'primevue/datepicker';
import Dialog from 'primevue/dialog';
import Divider from 'primevue/divider';
import Fieldset from 'primevue/fieldset';
import Panel from 'primevue/panel';
import Password from 'primevue/password';
import ProgressSpinner from 'primevue/progressspinner';
import Popover from 'primevue/popover';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import InputOtp from 'primevue/inputotp';
import Rating from 'primevue/rating';
import RadioButton from "primevue/radiobutton";
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import StepPanels from 'primevue/steppanels';
import StepItem from 'primevue/stepitem';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';
import Select from "primevue/select";
import Skeleton from 'primevue/skeleton';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import Tooltip from 'primevue/tooltip';
import ToggleSwitch from 'primevue/toggleswitch';
import ToggleButton from 'primevue/togglebutton';

// PrimeVue Datatable
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import ToastService from 'primevue/toastservice';

const app = createApp(App);

Sentry.init({
    app,
    dsn: "https://009076f34e4c446411bf77beb89d7ebe@o4506069475196928.ingest.us.sentry.io/4510594098987008",
    sendDefaultPii: true
});

const pinia = createPinia();

app.use(VueReCaptcha, {
    siteKey: import.meta.env.VITE_RECAPTCHA_CLIENT_KEY,
    loaderOptions: { autoHideBadge: true }
})

app.directive('tooltip', Tooltip);
app.use(ToastService);
app.use(ConfirmationService);

app.component('LucideIcon', LucideIcon)

app.component('Accordion', Accordion);
app.component('AccordionPanel', AccordionPanel);
app.component('AccordionHeader', AccordionHeader);
app.component('AccordionContent', AccordionContent);
app.component('Avatar', Avatar);
app.component('Button', Button);
app.component('BlockUI', BlockUI);
app.component('Card', Card);
app.component('Chart', Chart);
app.component('ConfirmDialog', ConfirmDialog);
app.component('DatePicker', DatePicker);
app.component('Dialog', Dialog);
app.component('Divider', Divider);
app.component('Fieldset', Fieldset);
app.component('IconField', IconField);
app.component('InputIcon', InputIcon);
app.component('InputText', InputText);
app.component('InputNumber', InputNumber);
app.component('InputOtp', InputOtp);
app.component('Panel', Panel);
app.component('Password', Password);
app.component('ProgressSpinner', ProgressSpinner);
app.component('Popover', Popover);
app.component('Rating', Rating);
app.component('RadioButton', RadioButton);
app.component('Select', Select);
app.component('StepList', StepList);
app.component('Stepper', Stepper);
app.component('StepPanels', StepPanels);
app.component('StepItem', StepItem);
app.component('Step', Step);
app.component('StepPanel', StepPanel);
app.component('Skeleton', Skeleton);
app.component('Tabs', Tabs);
app.component('TabList', TabList);
app.component('Tab', Tab);
app.component('TabPanels', TabPanels);
app.component('TabPanel', TabPanel);
app.component('ToggleButton', ToggleButton);
app.component('ToggleSwitch', ToggleSwitch);

app.component('DataTable', DataTable);
app.component('Column', Column);
app.component('ColumnGroup', ColumnGroup);
app.component('Row', Row);


const Noir = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{zinc.50}',
            100: '{zinc.100}',
            200: '{zinc.200}',
            300: '{zinc.300}',
            400: '{zinc.400}',
            500: '{zinc.500}',
            600: '{zinc.600}',
            700: '{zinc.700}',
            800: '{zinc.800}',
            900: '{zinc.900}',
            950: '{zinc.950}'
        },
        colorScheme: {
            light: {
                primary: {
                    color: '{zinc.950}',
                    inverseColor: '#ffffff',
                    hoverColor: '{zinc.900}',
                    activeColor: '{zinc.800}'
                },
                highlight: {
                    background: '{zinc.950}',
                    focusBackground: '{zinc.700}',
                    color: '#ffffff',
                    focusColor: '#ffffff'
                }
            },
            dark: {
                primary: {
                    color: '{zinc.50}',
                    inverseColor: '{zinc.950}',
                    hoverColor: '{zinc.100}',
                    activeColor: '{zinc.200}'
                },
                highlight: {
                    background: 'rgba(250, 250, 250, .16)',
                    focusBackground: 'rgba(250, 250, 250, .24)',
                    color: 'rgba(255,255,255,.87)',
                    focusColor: 'rgba(255,255,255,.87)'
                }
            }
        }
    }
});

const OrangeNoir = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{orange.50}',
            100: '{orange.100}',
            200: '{orange.200}',
            300: '{orange.300}',
            400: '{orange.400}',
            500: '{orange.500}',
            600: '{orange.600}',
            700: '{orange.700}',
            800: '{orange.800}',
            900: '{orange.900}',
            950: '{orange.950}'
        },
        colorScheme: {
            light: {
                primary: {
                    color: '{orange.600}',      // Darkest orange for text/buttons
                    inverseColor: '#ffffff',    // White for contrast
                    hoverColor: '{orange.900}', // Slightly lighter on hover
                    activeColor: '{orange.800}' // Even lighter when active
                },
                highlight: {
                    background: '{orange.950}', // Dark orange for highlighted backgrounds
                    focusBackground: '{orange.700}', // Lighter orange on focus
                    color: '#ffffff',           // White text on orange
                    focusColor: '#ffffff'       // White text on focus
                }
            },
            dark: {
                primary: {
                    color: '{orange.400}',       // Lightest orange for text/buttons
                    inverseColor: '{orange.950}', // Darkest orange for contrast
                    hoverColor: '{orange.700}', // Slightly darker on hover
                    activeColor: '{orange.200}' // Darker still when active
                },
                highlight: {
                    background: 'rgba(251, 146, 60, .16)', // Semi-transparent orange-400
                    focusBackground: 'rgba(251, 146, 60, .24)', // Slightly more opaque
                    color: 'rgba(255, 255, 255, .87)', // Near-white text
                    focusColor: 'rgba(255, 255, 255, .87)' // Near-white text on focus
                }
            }
        }
    }
});

function initializeApp() {

    app.use(pinia);
    app.use(router);
    app.use(plugin, config);

    app.use(PrimeVue, {
        theme: {
            preset: OrangeNoir,
            options: {
                prefix: 'p',
                darkModeSelector: '.my-app-dark',
                cssLayer: {
                    name: 'primevue',
                    order: 'tailwind-base, primevue, tailwind-utilities'
                }
            }
        }
    });
    
    // Wait for router to be ready before mounting
    router.isReady().then(() => {
        app.mount('#app');
    }).catch(error => {
        console.error('[Main] Failed to initialize router:', error);
    });
}

// Start the application
initializeApp();